import { Routes, Route } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import type { ReactElement } from 'react';
import { AuthGuard } from '@/components/layout/AuthGuard';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Spinner } from '@/components/ui/Spinner';
import { routes } from '@/router.config';
import type { RouteConfig } from '@/router.config';

const PageFallback = () => (
  <div className="flex h-[60vh] items-center justify-center">
    <Spinner size="lg" />
  </div>
);

/**
 * Recursively renders routes from the route registry. QA #27: this
 * replaces the previous inline lazy imports (~70 scattered across the
 * file) with a centralized route registry in router.config.ts.
 */
const renderRoute = (route: RouteConfig): ReactElement => {
  const Component = lazy(route.component);

  if (route.children) {
    return (
      <Route key={route.path} path={route.path} element={<Component />}>
        {route.children.map(renderRoute)}
      </Route>
    );
  }

  if (route.index) {
    return <Route key="index" index element={<Component />} />;
  }

  return <Route key={route.path} path={route.path} element={<Component />} />;
};

export const Router = () => {
  // Split routes into auth (no layout) and protected (with layout)
  const loginRoute = routes.find((r) => r.path === '/login');
  const notFoundRoute = routes.find((r) => r.path === '*');
  const protectedRoutes = routes.filter((r) => r.path !== '/login' && r.path !== '*');

  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        {loginRoute && renderRoute(loginRoute)}

        <Route
          path="/"
          element={
            <AuthGuard>
              <AdminLayout />
            </AuthGuard>
          }
        >
          {protectedRoutes.map(renderRoute)}
          {notFoundRoute && renderRoute(notFoundRoute)}
        </Route>
      </Routes>
    </Suspense>
  );
};
